<template>
  <SectionTemplate
    title="打造您的专属声音资产，为品牌注入灵魂"
    desc="在信息过载的时代，品牌专属声音是穿透噪音的利器。我们提供从个人声音克隆到企业级音色定制的全套方案，将无形声波转化为可沉淀、可复用的品牌核心资产。"
    sub-title="个性化编辑声音"
    theme="dark"
    class="tone-page-customization"
  >
    <div class="tone-page-customization__feature">
      <EditCard v-for="(feat, index) in featureList" :key="index" :data="feat">
        <template v-if="feat.btnProps" #btn>
          <ArrowBtn v-bind="feat.btnProps" />
        </template>
      </EditCard>
    </div>
    <div class="tone-page-customization__consult">
      <div class="tone-page-customization__consult-text">
        <div class="tone-page-customization__consult-title">声音定制</div>
        <p class="tone-page-customization__consult-desc">
          如果现有音色无法满足您的品牌定位，我们提供专业的官方音色定制服务。根据您的需求，从零开始为您创造独一无二的优质音色，成为您的核心品牌资产，强化品牌辨识度。
        </p>
        <ArrowBtn
          class="tone-page-customization__consult-btn"
          text="咨询定制方案"
          variant="purple-common"
          @click="$emit('consult')"
        />
      </div>
      <img
        :src="
          getFeUrl(
            '/youyan-platform/ai-voice/personalization/voice-personalize.png?x-oss-process=image/format,webp',
          )
        "
        class="tone-page-customization__consult-media"
        alt="有言支持企业级的AI声音定制服务"
      />
    </div>
  </SectionTemplate>
</template>

<script setup lang="ts">
import { addOpus } from '~/api';
import ArrowBtn from '~/design/components/buttons/arrow-btn.vue';
import EditCard from '~/design/components/edit-card/index.vue';
import SectionTemplate from '~/design/widgets/landing-section/cn-template.vue';

const handleCloneClick = () => {
  const userInfo = useUserInfo();
  if (!userInfo.value) {
    navigateTo(
      `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace/lab`))}`,
    );
  } else {
    location.href = '/workspace/lab';
  }
};

const handleEditClick = () => {
  // 未登录跳转登录
  const userInfo = useUserInfo();
  if (!userInfo.value) {
    // 接入场景和角色等接口
    loginTask.add({
      type: IAfterLoginActionType.CREATE_WORK_WITH_PRESET,
      payload: {},
    });
    navigateTo(
      `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace`))}`,
    );
    return;
  }

  return addOpus({}).then(result => {
    if (result) {
      const data = result.data as any;
      window.open(`/editor/${data.id}/${data.secret_id}`, '_blank');
    }
  });
};
const featureList = [
  {
    title: '声音克隆',
    des: '仅需10秒的音频样本，即可高保真复刻您或品牌代言人的独特声线。生成的声音情感丰富、自然流畅，完美应用于视频讲解、品牌代言等场景，打造真正属于您的AI分身。',
    path: getFeUrl(
      '/youyan-platform/ai-voice/personalization/voice-clone.png?x-oss-process=image/format,webp',
    ),
    alt: '有言提供超仿真的AI声音克隆',
    btnProps: {
      text: '立即克隆我的声音',
      variant: 'purple-common' as const,
      onClick: handleCloneClick,
    },
  },
  {
    title: '声音编辑',
    des: '生成语音后，您仍可拥有完整的控制权。随心调整语速、停顿、重音和语调，对不满意片段进行重新生成，像打磨文本一样精细优化音频，确保最终效果百分之百符合您的要求。',
    path: getFeUrl(
      '/youyan-platform/ai-voice/personalization/voice-edit.png?x-oss-process=image/format,webp',
    ),
    alt: '有言的AI配音支持自定义编辑',
    btnProps: {
      text: '立即体验编辑',
      variant: 'purple-common' as const,
      onClick: handleEditClick,
    },
  },
];
</script>

<style lang="scss" scoped>
.tone-page-customization {
  background-color: var(--x-text-1);
}
.tone-page-customization__feature {
  display: flex;
  gap: 40px;
  .edit-card {
    background-color: var(--x-purple-1);
  }
}
.tone-page-customization__consult {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 32px 56px;
  gap: 56px;
  border-radius: 12px;
  background-color: var(--x-purple-1);
  margin-top: 40px;
}
.tone-page-customization__consult-text {
  display: flex;
  flex-direction: column;
  gap: 32px;
  flex: 1;
  align-content: flex-start;
}
.tone-page-customization__consult-title {
  color: var(--x-text-1);
  font-size: var(--x-text-24-size);
  font-weight: 400;
  line-height: normal;
}
.tone-page-customization__consult-desc {
  color: var(--x-text-5);
  font-size: var(--x-text-16-size);
  font-weight: 400;
  line-height: 28px;
  margin: 0;
}
.tone-page-customization__consult-media {
  flex: 1;
  border-radius: 12px;
  aspect-ratio: 4/3;
  object-fit: cover;
  height: auto;
  width: 47%;
}
.tone-page-customization__consult-btn {
  width: fit-content;
}
</style>
<style lang="scss" src="./mobile.scss" scoped />
